/**
 * /pricing — marketing pricing page for the prepaid wallet model.
 *
 * Subscriptions (Pro/Power/Max monthly + 7-day trial) have been retired.
 * Pricing is now a prepaid wallet: sign up and get $5 in credit, pay only
 * for what you actually use, the balance never expires, and you top up
 * whenever you like ($5 minimum) — all inside the app, never from here.
 *
 * So this page is an *explainer*, not a checkout funnel: a hero, three
 * selling points (instead of three tier cards), a prepaid-focused Q&A,
 * and a final CTA that just gets people into the app (`/`). There are no
 * `/checkout?tier=` links anymore — top-ups happen in Settings → Billing
 * or the low-balance prompt once you're signed in.
 *
 * Routed in App.tsx behind the auth-out gate at `window.location.pathname
 * === '/pricing'`. Sets its own `<title>` + meta description via
 * MarketingShell.
 */
import { useTranslation } from 'react-i18next';
import { MarketingShell } from './MarketingShell';

export function PricingPage() {
  const { t } = useTranslation();

  return (
    <MarketingShell
      activeRoute="/pricing"
      currentLocale={typeof window !== 'undefined' && window.location.pathname.startsWith('/zh/') ? 'zh' : 'en'}
      hreflangPath="/pricing"
      documentTitle={t('pricing.pageTitle')}
      documentDescription={t('pricing.pageDescription')}
    >
      {/* ── Hero ── */}
      <section
        className="hero"
        style={{ paddingTop: 88, paddingBottom: 56 }}
      >
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">{t('pricing.heroLabel')}</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(48px, 6vw, 80px)', lineHeight: 1, margin: '12px 0 24px' }}
          >
            {t('pricing.prepaidHeroTitle')}
            <br />
            <em>{t('pricing.prepaidHeroTitleEm')}</em>
          </h1>
          <p className="lede" style={{ maxWidth: 640 }}>{t('pricing.prepaidLede')}</p>
          <div className="actions" style={{ marginTop: 28 }}>
            <a className="btn-light" href="/">{t('pricing.prepaidHeroCta')}</a>
          </div>
        </div>
      </section>

      {/* ── Three selling points (replaces the three tier cards) ── */}
      <section id="pricing" className="pricing" style={{ paddingTop: 0 }}>
        <div className="head">
          <h2 className="serif">
            {t('pricing.pointsHeadingPart1')} <em>{t('pricing.pointsHeadingEm')}</em>
          </h2>
          <div className="softcap">{t('pricing.pointsSubhead')}</div>
        </div>
        <div className="pricing-grid">
          <div className="tier">
            <div className="tname">{t('pricing.point1Title')}</div>
            <div className="price serif">$5</div>
            <div className="quota">{t('pricing.point1Tag')}</div>
            <p style={{ marginTop: 12, color: 'var(--ink-2)' }}>{t('pricing.point1Body')}</p>
          </div>
          <div className="tier">
            <div className="tname">{t('pricing.point2Title')}</div>
            <div className="quota">{t('pricing.point2Tag')}</div>
            <p style={{ marginTop: 12, color: 'var(--ink-2)' }}>{t('pricing.point2Body')}</p>
          </div>
          <div className="tier">
            <div className="tname">{t('pricing.point3Title')}</div>
            <div className="quota">{t('pricing.point3Tag')}</div>
            <p style={{ marginTop: 12, color: 'var(--ink-2)' }}>{t('pricing.point3Body')}</p>
          </div>
        </div>
      </section>

      {/* ── Prepaid Q&A ── */}
      <section className="voices" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">{t('pricing.qaHeading')}</h2>
        </div>
        <div className="voices-grid" style={{ gridTemplateColumns: '1fr', maxWidth: 760 }}>
          <div className="faq">
            <details>
              <summary>{t('pricing.qaPrepaid1Q')}</summary>
              <p>{t('pricing.qaPrepaid1A')}</p>
            </details>
            <details>
              <summary>{t('pricing.qaPrepaid2Q')}</summary>
              <p>{t('pricing.qaPrepaid2A')}</p>
            </details>
            <details>
              <summary>{t('pricing.qaPrepaid3Q')}</summary>
              <p>{t('pricing.qaPrepaid3A')}</p>
            </details>
            <details>
              <summary>{t('pricing.qaPrepaid4Q')}</summary>
              <p>{t('pricing.qaPrepaid4A')}</p>
            </details>
            <details>
              <summary>{t('pricing.qaPrepaid5Q')}</summary>
              <p>{t('pricing.qaPrepaid5A')}</p>
            </details>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="final">
        <h2 className="serif">
          {t('homepage.finalHeadingLine1')}
          <br />
          <em>{t('homepage.finalHeadingLine2')}</em>
        </h2>
        <p>{t('pricing.prepaidFinalBody')}</p>
        <div className="actions">
          <a className="btn-light" href="/">{t('homepage.finalCta')}</a>
        </div>
      </section>
    </MarketingShell>
  );
}
